/**
 * NotificationBell — bell icon with unread badge + dropdown panel for admins/super-admins.
 *
 * Polls for notifications every 30s (list) and unread count every 15s.
 * Only visible to admins and super-admins (role check done server-side).
 */

import { Bell } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { formatDistanceToNow } from "date-fns";

import {
  useNotifications,
  useUnreadCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from "@/hooks/useNotifications";
import { useAuthStore } from "@/store/auth";

export function NotificationBell() {
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const memberships = useAuthStore((s) => s.memberships);

  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Check if user is admin or super-admin
  const isAdmin = isSuperAdmin || memberships.some((m) => m.role === "admin");

  // Only fetch if user is admin/super-admin (avoids 403 polling for employees).
  const { data: unreadCount = 0 } = useUnreadCount(isAdmin);
  const { data: notifications = [] } = useNotifications(50, false, isAdmin);
  const markReadMutation = useMarkNotificationRead();
  const markAllReadMutation = useMarkAllNotificationsRead();

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Only render for admins and super-admins
  if (!user || !isAdmin) return null;

  const handleMarkAsRead = async (notificationId: string) => {
    await markReadMutation.mutateAsync(notificationId);
  };

  const handleMarkAllAsRead = async () => {
    await markAllReadMutation.mutateAsync();
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
        title="Notifications"
        style={bellButton}
      >
        <Bell size={16} aria-hidden />
        {unreadCount > 0 && (
          <span style={badge} aria-label={`${unreadCount} unread notifications`}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div ref={panelRef} style={panel}>
          <div style={header}>
            <h3 style={title}>Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                style={markAllButton}
                disabled={markAllReadMutation.isPending}
              >
                {markAllReadMutation.isPending ? "Marking..." : "Mark all read"}
              </button>
            )}
          </div>

          <div style={scrollContainer}>
            {notifications.length === 0 ? (
              <div style={emptyState}>
                <Bell size={32} style={{ color: "var(--mute)", opacity: 0.5 }} />
                <p style={{ fontSize: 13, color: "var(--mute)", margin: "8px 0 0" }}>
                  No notifications yet
                </p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  style={{
                    ...notificationItem,
                    background: notif.is_read ? "transparent" : "var(--surface-1)",
                  }}
                  onClick={() => {
                    if (!notif.is_read) handleMarkAsRead(notif.id);
                  }}
                >
                  <div style={notificationContent}>
                    <div style={notificationHeader}>
                      <span
                        style={{
                          ...typeLabel,
                          background:
                            notif.notification_type === "commit"
                              ? "var(--accent-purple-fill)"
                              : notif.notification_type === "announcement"
                                ? "var(--accent-blue-fill)"
                                : "var(--surface-2)",
                        }}
                      >
                        {notif.notification_type}
                      </span>
                      <span style={timestamp}>
                        {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                      </span>
                    </div>

                    <h4 style={notificationTitle}>{notif.title}</h4>
                    <p style={notificationMessage}>{notif.message}</p>

                    {/^https?:\/\//i.test(notif.commit_url ?? "") && (
                      <a
                        href={notif.commit_url ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={commitLink}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {notif.commit_sha?.slice(0, 7) ?? "View commit"}
                      </a>
                    )}

                    {!notif.is_read && <div style={unreadDot} />}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const bellButton: CSSProperties = {
  position: "relative",
  width: 32,
  height: 32,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  color: "var(--body)",
  cursor: "pointer",
  padding: 0,
};

const badge: CSSProperties = {
  position: "absolute",
  top: -4,
  right: -4,
  minWidth: 18,
  height: 18,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--accent-orange-fill)",
  color: "var(--ink)",
  fontSize: 10,
  fontWeight: 600,
  borderRadius: 9999,
  padding: "0 4px",
  border: "1.5px solid var(--canvas)",
};

const panel: CSSProperties = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  width: 380,
  maxHeight: 480,
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  display: "flex",
  flexDirection: "column",
  zIndex: 100,
};

const header: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  borderBottom: "1px solid var(--hairline)",
};

const title: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  margin: 0,
  color: "var(--ink)",
};

const markAllButton: CSSProperties = {
  background: "transparent",
  border: "none",
  fontSize: 12,
  color: "var(--accent-purple-text)",
  cursor: "pointer",
  padding: "4px 8px",
  borderRadius: 4,
};

const scrollContainer: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  maxHeight: 420,
};

const emptyState: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 24px",
};

const notificationItem: CSSProperties = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--hairline)",
  cursor: "pointer",
  transition: "background 0.15s ease",
  position: "relative",
};

const notificationContent: CSSProperties = {
  position: "relative",
};

const notificationHeader: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 6,
};

const typeLabel: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  textTransform: "uppercase",
  padding: "2px 6px",
  borderRadius: 4,
  letterSpacing: "0.5px",
  color: "var(--ink)",
};

const timestamp: CSSProperties = {
  fontSize: 11,
  color: "var(--mute)",
  fontFamily: "var(--font-mono)",
};

const notificationTitle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  margin: "0 0 4px",
  color: "var(--ink)",
};

const notificationMessage: CSSProperties = {
  fontSize: 12,
  color: "var(--body)",
  margin: 0,
  lineHeight: 1.5,
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
};

const commitLink: CSSProperties = {
  fontSize: 11,
  color: "var(--accent-purple-text)",
  fontFamily: "var(--font-mono)",
  textDecoration: "none",
  marginTop: 6,
  display: "inline-block",
};

const unreadDot: CSSProperties = {
  position: "absolute",
  top: 0,
  right: 0,
  width: 8,
  height: 8,
  background: "var(--accent-orange-fill)",
  borderRadius: 9999,
  border: "1.5px solid var(--canvas)",
};
